import * as React from "react";
import {VeboxTreeView, VeboxTreeViewInputItemProps} from "../VeboxTreeView";
import {VeboxSelectionListActionType} from "../../selection_list/VeboxSelectionList";
import {VeboxTreeViewItemProps} from "./VeboxTreeViewItem";
import {useValue} from "../../../users/HierarchyVisibilityUtils";

export interface CheckboxState {
    value: any;
    checked: boolean;
    indeterminate: boolean;
    children: CheckboxState[] | any[];
}

export const VeboxTreeViewCheckbox: React.FC<VeboxTreeViewInputItemProps & VeboxTreeViewItemProps & React.HTMLAttributes<any>> = ({
                                                                                                                                      checked,
                                                                                                                                      indeterminate,
                                                                                                                                      children,
                                                                                                                                      value,
                                                                                                                                      onChange,
                                                                                                                                      siblings,
                                                                                                                                      label,
                                                                                                                                      disabled,
                                                                                                                                      className,
                                                                                                                                      ...rest
                                                                                                                                  }) => {

    const [state, setState] = React.useState<CheckboxState>({
        value,
        checked,
        indeterminate,
        children: [],
    });

    const childrenValues = React.Children.map(children, (child: any) => child?.props?.value);
    const numberOfChildren = React.Children.count(children);

    const handleCheckboxChange = (val, action: VeboxSelectionListActionType) => {
        switch (action) {
            case VeboxSelectionListActionType.ADD: {
                const currentNodeChildren = [...state.children.filter(v => useValue(v) !== useValue(val)), val]
                const checkedChildren = currentNodeChildren.filter(
                    child => typeof child === "string" || child.checked);
                const payload: CheckboxState = {
                    value,
                    checked: checkedChildren.length === numberOfChildren,
                    indeterminate: 0 < currentNodeChildren.length && checkedChildren.length !== numberOfChildren,
                    children: currentNodeChildren,
                }
                onChange(payload, VeboxSelectionListActionType.ADD);
                break;
            }
            case VeboxSelectionListActionType.REMOVE: {
                const currentNodeChildren = [...state.children.filter(v => useValue(v) !== useValue(val))];
                const checkedChildren = currentNodeChildren.filter(
                    child => typeof child === "string" || child.checked);
                const payload: CheckboxState = {
                    value,
                    checked: checkedChildren.length === numberOfChildren,
                    indeterminate: 0 < currentNodeChildren.length && checkedChildren.length !== numberOfChildren,
                    children: currentNodeChildren,
                }
                onChange(payload,
                    payload.children.length === 0 ? VeboxSelectionListActionType.REMOVE : VeboxSelectionListActionType.ADD); // Adding is like updating
                break;
            }
        }
    };

    const nodeChange = (e) => {
        if (e.target.checked) {
            onChange(value, VeboxSelectionListActionType.ADD);
        } else {
            onChange(value, VeboxSelectionListActionType.REMOVE);
        }
    };

    const ref = React.useRef<HTMLInputElement>();

    React.useEffect(() => {
        const newState = siblings.find(v => useValue(v) === value);
        if (typeof newState === 'object') {
            ref.current.indeterminate = newState.indeterminate;
            setState(newState);
        } else if (typeof newState === 'string') {

            const temp = {
                value: useValue(newState),
                checked: true,
                indeterminate: false,
                children: childrenValues,
            };
            ref.current.indeterminate = false;
            setState(temp);
        } else {
            const temp = {
                value: useValue(newState),
                checked: false,
                indeterminate: false,
                children: [],
            };
            ref.current.indeterminate = false;
            setState(temp);
        }
    }, [siblings]);

    return (
        <VeboxTreeView.Item
            {...({
                ...rest,
                parentValue: undefined,
                parentState: undefined,
                onChange: undefined,
            })}
            className={className + (disabled ? " vebox-tree-view-disabled" : "")}
            label={
                <>
                    <input
                        type={"checkbox"}
                        value={value}
                        checked={state.checked}
                        disabled={disabled}
                        onChange={nodeChange}
                        className={"checkbox"}
                        ref={ref}
                    />
                    {label}
                </>
            }
        >
            {React.Children.toArray(children)
                .filter(Boolean)
                .map((child: any) => {
                    return React.cloneElement(
                        child,
                        {
                            ...child.props,
                            onChange: handleCheckboxChange,
                            siblings: state.children ?? [],
                            className: child?.props?.className + className + ((disabled || child?.props?.disabled) ? " vebox-tree-view-disabled" : ""),
                            disabled: disabled || child?.props?.disabled,
                        }
                    )
                })
            }
        </VeboxTreeView.Item>
    );
}